import { useMutation, MutationTuple, MutationUpdaterFn, ApolloError, Reference } from '@apollo/client';
import { CREATE_USER, DELETE_USER, SEND_INVITE } from './queries';
import { IApiUser } from '../users/types';
import { ApiAdminUser } from './types';
import { Country, Role } from '@tendium/prom-types';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { notification } from 'src/common';
import { AdminUsersDataCached } from './Users/types';

export function useSendInvite(): MutationTuple<{ createInviteUserRequest: IApiUser }, { email: string }> {
  return useMutation(SEND_INVITE);
}

type CreateUserData = { createUser: ApiAdminUser };
type CreateUserVars = {
  email: string;
  name?: string;
  lastName?: string;
  password?: string;
  roles?: Role[];
  companyId?: string;
  inviteSubUser?: boolean;
  country: Country;
};
type UseCreateUserVars = CreateUserVars & { onComplete?: () => void };
type UseCreateUser = [(vars: UseCreateUserVars) => void, { loading: boolean; error?: ApolloError }];
export function useCreateUser(): UseCreateUser {
  const { t } = useTranslation();
  const [createUser, { loading, error }] = useMutation<CreateUserData, CreateUserVars>(CREATE_USER);

  const createUserFn = useCallback(
    (vars: UseCreateUserVars) => {
      const { onComplete, ...variables } = vars;
      createUser({
        variables: { ...variables, inviteSubUser: variables.companyId ? true : undefined },
        update: updateCacheOnCreateUser(variables.companyId)
      })
        .then(() => {
          onComplete?.();
        })
        .catch(() => {
          notification.error({
            message: t('Teammates.employeeHasNotAdded'),
            description: t('Common.unknownErrorDesc')
          });
        });
    },
    [createUser, t]
  );
  return useMemo(() => [createUserFn, { loading, error }], [createUserFn, error, loading]);
}
export function updateCacheOnCreateUser(companyId?: string): MutationUpdaterFn<CreateUserData> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const {
      createUser: { email, company }
    } = data;
    cache.modify({
      fields: {
        searchUser(cached: AdminUsersDataCached, { toReference }) {
          const { variables } = cached;
          if (
            (variables?.email && email.toLowerCase().includes(variables.email.toLowerCase())) ||
            (variables?.companyId && company.id.toLowerCase().includes(variables.companyId.toLowerCase())) ||
            (!variables.email && !variables.companyId)
          ) {
            return {
              ...cached,
              value: {
                ...cached.value,
                count: cached.value.count + 1,
                users: [...cached.value.users, toReference({ __typename: 'User', email })]
              }
            };
          } else {
            return cached;
          }
        }
      }
    });
    if (companyId) {
      cache.modify({
        id: cache.identify({
          __typename: 'Company',
          id: companyId
        }),
        fields: {
          personel(existingRefs: Reference[], { toReference, readField }) {
            return existingRefs.some(ref => readField('email', ref) === email)
              ? existingRefs
              : [
                  ...existingRefs,
                  toReference({
                    __typename: 'User',
                    email
                  })
                ];
          }
        }
      });
    }
  };
}

type DeleteUserData = { deleteUser: boolean };
type DeleteUserVars = { email: string };
type UseDeleteUserVars = DeleteUserVars & { companyId?: string };
type UseDeleteUser = [(vars: UseDeleteUserVars) => void, { loading: boolean; error?: ApolloError }];
export function useDeleteUser(): UseDeleteUser {
  const { t } = useTranslation();
  const [deleteUser, { loading, error }] = useMutation<DeleteUserData, DeleteUserVars>(DELETE_USER);

  const deleteUserFn = useCallback(
    (vars: UseDeleteUserVars) => {
      const { email } = vars;
      deleteUser({
        variables: { email },
        update: updateCacheOnDeleteUser(vars)
      }).catch(() => {
        notification.error({
          description: t('Common.unknownErrorDesc'),
          message: t('Common.unknownError')
        });
      });
    },
    [deleteUser, t]
  );
  return useMemo(() => [deleteUserFn, { loading, error }], [deleteUserFn, error, loading]);
}
export function updateCacheOnDeleteUser(vars: UseDeleteUserVars): MutationUpdaterFn<DeleteUserData> {
  const { email, companyId } = vars;
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const { deleteUser } = data;
    cache.modify({
      fields: {
        searchUser(cached: AdminUsersDataCached, { readField }) {
          if (deleteUser && cached.value.users.some(user => readField({ fieldName: 'email', from: user }) === email)) {
            return {
              ...cached,
              value: {
                ...cached.value,
                count: Math.max(0, cached.value.count - 1) ?? 0,
                users: cached.value.users.filter(user => readField({ fieldName: 'email', from: user }) !== email)
              }
            };
          } else {
            return cached;
          }
        }
      }
    });
    if (companyId) {
      cache.modify({
        id: cache.identify({
          __typename: 'Company',
          id: companyId
        }),
        fields: {
          personel(existingRefs: Reference[], { readField }) {
            return existingRefs.filter(ref => readField('email', ref) !== email);
          }
        }
      });
    }
    cache.evict({ id: cache.identify({ email, __typename: 'User' }) });
    cache.gc();
  };
}
